import React, { useState, useEffect } from 'react';
import { Clock, Plus, Edit2, Trash2, Save, X, Calendar } from 'lucide-react';

const RestaurantHoursUI = () => {
  const [hours, setHours] = useState([]);
  const [editingHour, setEditingHour] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const daysOfWeek = [
    { value: 0, label: 'Pazar' },
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' }
  ];

  const [formData, setFormData] = useState({
    restaurantId: '',
    dayOfWeek: 0,
    openTime: '',
    closeTime: '',
    isClosed: false
  });

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Load hours from API
  useEffect(() => {
    fetchHours();
  }, []);

  const fetchHours = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/RestaurantHours`);
      if (response.ok) {
        const data = await response.json();
        setHours(data);
      } else {
        setError('Çalışma saatleri yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      dayOfWeek: 0,
      openTime: '',
      closeTime: '',
      isClosed: false
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (hour) => {
    setEditingHour(hour.hourId);
    setFormData({
      restaurantId: hour.restaurantId,
      dayOfWeek: hour.dayOfWeek,
      openTime: hour.openTime,
      closeTime: hour.closeTime,
      isClosed: hour.isClosed
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.restaurantId) {
        setError('Restoran seçimi zorunludur');
        return;
      }

      if (!formData.isClosed && (!formData.openTime || !formData.closeTime)) {
        setError('Açılış ve kapanış saatleri zorunludur');
        return;
      }

      if (!formData.isClosed && formData.openTime >= formData.closeTime) {
        setError('Kapanış saati açılış saatinden sonra olmalıdır');
        return;
      }

      // Check if day already exists for this restaurant
      const existingDay = hours.find(h => 
        h.restaurantId === formData.restaurantId && 
        h.dayOfWeek === parseInt(formData.dayOfWeek) &&
        h.hourId !== editingHour
      );

      if (existingDay) {
        setError('Bu gün için zaten çalışma saati tanımlanmış');
        return;
      }

      const requestData = {
        restaurantId: formData.restaurantId,
        dayOfWeek: parseInt(formData.dayOfWeek),
        openTime: formData.isClosed ? null : formData.openTime + ':00', // Add seconds
        closeTime: formData.isClosed ? null : formData.closeTime + ':00', // Add seconds
        isClosed: formData.isClosed
      };

      let response;
      
      if (editingHour) {
        // Update existing hour
        response = await fetch(`${API_BASE_URL}/api/RestaurantHours/${editingHour}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Create new hour
        response = await fetch(`${API_BASE_URL}/api/RestaurantHours`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      }

      if (response.ok) {
        // Refresh the list
        await fetchHours();
        setIsCreating(false);
        setEditingHour(null);
        resetForm();
      } else {
        const errorData = await response.text();
        setError(errorData || 'İşlem sırasında hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingHour(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (hourId) => {
    if (window.confirm('Bu çalışma saatini silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/RestaurantHours/${hourId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          // Refresh the list
          await fetchHours();
        } else {
          setError('Silme işlemi sırasında hata oluştu');
        }
      } catch (err) {
        setError('API bağlantı hatası');
        console.error('API Error:', err);
      }
    }
  };

  const getDayLabel = (dayOfWeek) => {
    return daysOfWeek.find(day => day.value === dayOfWeek)?.label || '';
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5); // HH:MM format
  };

  const sortedHours = [...hours].sort((a, b) => {
    // Sunday (0) should come last
    const dayA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
    const dayB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
    return dayA - dayB;
  });

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Çalışma Saatleri</h1>
                <p className="text-gray-600">Restoran çalışma saatlerini yönetin</p>
              </div>
            </div>
            {!isCreating && !editingHour && (
              <button
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Çalışma Saati</span>
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingHour) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingHour ? 'Çalışma Saatini Düzenle' : 'Yeni Çalışma Saati Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restoran ID
                </label>
                <input
                  type="text"
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  placeholder="Restoran ID girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gün
                </label>
                <select
                  name="dayOfWeek"
                  value={formData.dayOfWeek}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {daysOfWeek.map(day => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="isClosed"
                    name="isClosed"
                    checked={formData.isClosed}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isClosed" className="text-sm font-medium text-gray-700">
                    Bu gün kapalı
                  </label>
                </div>
              </div>

              {!formData.isClosed && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Açılış Saati
                    </label>
                    <input
                      type="time"
                      name="openTime"
                      value={formData.openTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kapanış Saati
                    </label>
                    <input
                      type="time"
                      name="closeTime"
                      value={formData.closeTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Hours List */}
        <div className="p-6">
          {sortedHours.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz çalışma saati tanımlanmamış</h3>
              <p className="text-gray-600 mb-4">İlk çalışma saatinizi ekleyerek başlayın</p>
              <button
                onClick={handleCreate}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                Çalışma Saati Ekle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedHours.map((hour) => (
                <div key={hour.hourId} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {getDayLabel(hour.dayOfWeek)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEdit(hour)}
                        className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(hour.hourId)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Restoran:</span> {hour.restaurantId}
                    </div>
                    
                    {hour.isClosed ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Kapalı
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTime(hour.openTime)} - {formatTime(hour.closeTime)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantHoursUI;